import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from './util';

export interface SegmentOption<T extends string | number> {
  value: T;
  label: ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string | number> {
  options: readonly SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Accessible group label. */
  label: string;
  /** Read-only rendering (non-host lobby view). */
  readOnly?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

/** Index of the next enabled option when moving `dir` from `from` (wraps around). */
export function nextEnabledIndex(disabled: readonly boolean[], from: number, dir: 1 | -1): number {
  const n = disabled.length;
  for (let step = 1; step <= n; step++) {
    const i = (((from + dir * step) % n) + n) % n;
    if (!disabled[i]) return i;
  }
  return from;
}

/**
 * Ink segmented control (radio group). Arrow keys move + select, Home/End jump; roving tabindex.
 */
export function SegmentedControl<T extends string | number>({
  options,
  value,
  onChange,
  label,
  readOnly,
  size = 'md',
  className,
}: SegmentedControlProps<T>) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const selectedIndex = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  );
  const disabled = options.map((o) => !!o.disabled || !!readOnly);

  const select = (i: number) => {
    const opt = options[i];
    if (!opt || disabled[i]) return;
    if (opt.value !== value) onChange(opt.value);
    refs.current[i]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (readOnly) return;
    let target: number | null = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown')
      target = nextEnabledIndex(disabled, selectedIndex, 1);
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp')
      target = nextEnabledIndex(disabled, selectedIndex, -1);
    else if (e.key === 'Home') target = nextEnabledIndex(disabled, -1 + options.length, 1);
    else if (e.key === 'End') target = nextEnabledIndex(disabled, 0, -1);
    if (target !== null) {
      e.preventDefault();
      select(target);
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label={label}
      aria-readonly={readOnly || undefined}
      onKeyDown={onKeyDown}
      className={cn(
        'inline-flex max-w-full flex-wrap border-3 border-ink bg-white shadow-ink-xs',
        readOnly && 'opacity-90',
        className,
      )}
    >
      {options.map((opt, i) => {
        const selected = i === selectedIndex;
        return (
          <button
            key={String(opt.value)}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-disabled={disabled[i] || undefined}
            tabIndex={selected ? 0 : -1}
            onClick={() => select(i)}
            className={cn(
              'relative font-extrabold leading-tight whitespace-nowrap transition-colors duration-100',
              i > 0 && 'border-l-2 border-ink',
              // Phones get finger-sized segments; the compact look starts at `sm`.
              size === 'sm'
                ? 'min-h-10 px-3 text-[13px] sm:min-h-0 sm:px-2 sm:py-0.5 sm:text-[12px]'
                : 'min-h-10 px-3 py-1 text-[14px] sm:min-h-0',
              selected ? 'bg-ink text-yellow' : 'bg-white text-ink',
              !selected && !disabled[i] && 'hover:bg-yellow-soft',
              !selected && opt.disabled && 'text-muted line-through',
              readOnly && 'cursor-default',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
